/** GET /scoreme/details/:director_uuid — one yearly breakdown row */
export type ScoremeDetailsRow = {
  id: number;
  director_uuid: string;
  reference_id: string;
  created_at: string;
  year: string;
  absconders: string;
  arrested: string;
  consumer_court: string;
  defaulters: string;
  e_court: string;
  fir: string;
  high_court: string;
  ibc: string;
  mca: string;
  most_wanted: string;
  supreme_court: string;
  total_case: string;
  tribunal: string;
};

/** `data` object from GET /scoreme/details/:director_uuid */
export type ScoremeDetailsData = {
  details: ScoremeDetailsRow[];
  excel_s3_url: string;
  message?: string;
  status?: number;
};

/** Full GET /scoreme/details/:director_uuid response */
export type ScoremeDetailsResponse = {
  status: number;
  message: string;
  data: ScoremeDetailsData;
};

export type ScoremeParticularKey =
  | "total"
  | "e_court"
  | "high_court"
  | "supreme_court"
  | "consumer_court"
  | "tribunal"
  | "ibc"
  | "arrested"
  | "fir"
  | "mca"
  | "most_wanted"
  | "absconders"
  | "defaulters";

export const SCOREME_PARTICULARS: { key: ScoremeParticularKey; label: string }[] = [
  { key: "total", label: "Total" },
  { key: "e_court", label: "eCourt" },
  { key: "high_court", label: "High Court" },
  { key: "supreme_court", label: "Supreme Court" },
  { key: "consumer_court", label: "Consumer Court" },
  { key: "tribunal", label: "Tribunal" },
  { key: "ibc", label: "IBC" },
  { key: "arrested", label: "Arrested" },
  { key: "fir", label: "FIR" },
  { key: "mca", label: "MCA" },
  { key: "most_wanted", label: "Most Wanted" },
  { key: "absconders", label: "Absconders" },
  { key: "defaulters", label: "Defaulters" },
];

const toCount = (value: string | number | null | undefined): number => {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
};

/** Value for a particulars row from one API year object. */
function particularValue(
  row: ScoremeDetailsRow,
  key: ScoremeParticularKey
): number {
  if (key === "total") return toCount(row.total_case);
  return toCount(row[key]);
}

function classifyYearLabel(year: string): "total" | "previous" | "numeric" | "skip" {
  const normalized = year.trim().toLowerCase();
  if (normalized === "total") return "total";
  // API may truncate as "All Previo"
  if (normalized.startsWith("all previo")) return "previous";
  if (/^\d{4}$/.test(normalized)) return "numeric";
  return "skip";
}

/** Recent calendar years shown as columns (inclusive of current year). */
export function getScoremeYearColumns(windowSize = 5): number[] {
  const current = new Date().getFullYear();
  return Array.from({ length: windowSize }, (_, i) => current - (windowSize - 1 - i));
}

export type ScoremeMatrix = {
  yearColumns: number[];
  cells: Record<ScoremeParticularKey, Record<string, number>>;
  /** From API objects with year === "Total" (not summed from other columns). */
  rowTotals: Record<ScoremeParticularKey, number>;
  grandTotal: number;
};

/**
 * Pivot scoreme details into the Legal History table.
 * - year "Total" → Total Cases column
 * - year "All Previo(us)" → All Previous column
 * - numeric years in the window → year columns
 * No client-side summing across years or categories.
 */
export function buildScoremeMatrix(
  rows: ScoremeDetailsRow[],
  yearColumns = getScoremeYearColumns()
): ScoremeMatrix {
  const yearSet = new Set(yearColumns.map(String));
  const emptyYearMap = (): Record<string, number> => {
    const map: Record<string, number> = { previous: 0 };
    for (const year of yearColumns) map[String(year)] = 0;
    return map;
  };

  const cells = {} as ScoremeMatrix["cells"];
  const rowTotals = {} as ScoremeMatrix["rowTotals"];
  for (const { key } of SCOREME_PARTICULARS) {
    cells[key] = emptyYearMap();
    rowTotals[key] = 0;
  }

  for (const row of rows) {
    const yearRaw = String(row.year ?? "");
    const kind = classifyYearLabel(yearRaw);

    if (kind === "total") {
      for (const { key } of SCOREME_PARTICULARS) {
        rowTotals[key] = particularValue(row, key);
      }
      continue;
    }

    if (kind === "previous") {
      for (const { key } of SCOREME_PARTICULARS) {
        cells[key].previous = particularValue(row, key);
      }
      continue;
    }

    if (kind === "numeric" && yearSet.has(String(Number(yearRaw)))) {
      const bucket = String(Number(yearRaw));
      for (const { key } of SCOREME_PARTICULARS) {
        cells[key][bucket] = particularValue(row, key);
      }
    }
  }

  return {
    yearColumns,
    cells,
    rowTotals,
    grandTotal: rowTotals.total,
  };
}
